/**
 * Route guard for admin-only CI artifact pages.
 *
 * Gates `/admin/ci-artifacts` and `/admin/ci-fixture` (and any future
 * admin tooling) behind the `sovereign` tier from `useAuth()`. The same
 * tier check is enforced server-side by the `ci-artifact-access` edge
 * function (which re-reads `user_roles` with the service role). This
 * client-side guard:
 *   1. Reads `tier` from `useAuth()` for an instant local check, AND
 *   2. Calls the edge function so a user who tampered with the cached
 *      `tier` can't render the viewer shell.
 * Both must pass before children render.
 *
 *   - loading  → spinner placeholder (don't flash a redirect)
 *   - no user  → redirect to /auth with a `?next=` param
 *   - server says non-sov → render an "access denied" card
 *   - sovereign → render children
 */
import { ReactNode, useEffect, useState } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ShieldAlert, Loader2, ChevronDown, ChevronRight, Clock3 } from "lucide-react";
import NavigationBar from "@/components/NavigationBar";

type ServerCheck =
  | { state: "idle" }
  | { state: "checking" }
  | { state: "ok" }
  | {
      state: "denied";
      reason: string;
      status: number | null;
      requestId: string | null;
      retryAfterSeconds: number | null;
      throttled: boolean;
    };

/**
 * Dev/preview-only escape hatch used by Playwright deep-link tests.
 * Only honored when:
 *   1. The bundle is NOT a production build (`import.meta.env.PROD === false`),
 *      AND
 *   2. The URL carries `?e2e_skip_guard=1`.
 * This means a production user appending the query param has no effect —
 * the server-side `ci-artifact-access` edge function is still the
 * authoritative gate.
 */
function isE2EBypass(searchParams: URLSearchParams): boolean {
  if (import.meta.env.PROD) return false;
  return searchParams.get("e2e_skip_guard") === "1";
}

export function RequireSovereign({ children }: { children: ReactNode }) {
  const { user, tier, loading } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [server, setServer] = useState<ServerCheck>({ state: "idle" });
  const e2eBypass = isE2EBypass(searchParams);
  const [reasonExpanded, setReasonExpanded] = useState(false);

  useEffect(() => {
    if (e2eBypass) return;
    if (loading || !user) return;
    let cancelled = false;
    setServer({ state: "checking" });
    (async () => {
      try {
        // Use functions.invoke for the body, but capture the raw HTTP
        // status from the underlying response (401 vs 403) so the UI
        // can show a consistent error code badge across both admin
        // routes. We also harvest the `request_id` and `Retry-After`
        // header so denials surface a correlation id and an explicit
        // throttle hint when applicable.
        const { data, error } = await supabase.functions.invoke(
          "ci-artifact-access",
          { body: {} },
        );
        if (cancelled) return;
        let status: number | null = null;
        let requestId: string | null = null;
        let retryAfterSeconds: number | null = null;
        const maybeCtx = (error as unknown as {
          context?: { status?: number; headers?: Headers };
        } | null)?.context;
        if (maybeCtx) {
          if (typeof maybeCtx.status === "number") status = maybeCtx.status;
          const hdrs = maybeCtx.headers;
          if (hdrs && typeof hdrs.get === "function") {
            requestId = hdrs.get("x-request-id");
            const ra = hdrs.get("retry-after");
            if (ra) {
              const n = Number(ra);
              if (Number.isFinite(n) && n >= 0) retryAfterSeconds = Math.round(n);
            }
          }
        }
        if (data && typeof data === "object") {
          const d = data as Record<string, unknown>;
          if (!requestId && typeof d.request_id === "string") {
            requestId = d.request_id;
          }
          if (status === null && "error" in d) {
            const errStr = String(d.error ?? "");
            if (errStr.startsWith("forbidden")) status = 403;
            else if (errStr.startsWith("unauthorized")) status = 401;
            else if (errStr.startsWith("rate_limited")) status = 429;
          }
          if (
            retryAfterSeconds === null &&
            typeof d.retry_after_seconds === "number"
          ) {
            retryAfterSeconds = Math.round(d.retry_after_seconds);
          }
        }
        if (error || !data?.ok) {
          const reason =
            (data && (data.error as string)) ||
            error?.message ||
            "server_denied";
          if (status === null) {
            if (reason.startsWith("forbidden")) status = 403;
            else if (reason.startsWith("unauthorized")) status = 401;
            else if (reason.startsWith("rate_limited")) status = 429;
          }
          const throttled =
            status === 429 || /rate.?limit|throttl/i.test(reason);
          setServer({
            state: "denied",
            reason,
            status,
            requestId,
            retryAfterSeconds,
            throttled,
          });
        } else {
          setServer({ state: "ok" });
        }
      } catch (e) {
        if (cancelled) return;
        setServer({
          state: "denied",
          reason: e instanceof Error ? e.message : "network_error",
          status: null,
          requestId: null,
          retryAfterSeconds: null,
          throttled: false,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loading, user, e2eBypass]);

  if (e2eBypass) {
    // Dev/preview only — production short-circuits to false in isE2EBypass.
    return <>{children}</>;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="require-sovereign-loading">
        <Loader2 className="w-6 h-6 text-sovereign-gold animate-spin" />
      </div>
    );
  }

  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth?next=${next}`} replace />;
  }

  // Either the local hint is wrong tier, or the server explicitly denied.
  // Loading the server check shows a spinner, not the viewer.
  if (tier !== "sovereign" || server.state === "denied") {
    const reason =
      server.state === "denied" ? server.reason : `local_tier=${tier ?? "unknown"}`;
    const statusCode: number | string =
      server.state === "denied" && typeof server.status === "number"
        ? server.status
        : tier && tier !== "sovereign"
          ? 403
          : "ERR";
    const statusLabel =
      typeof statusCode === "number"
        ? statusCode === 401
          ? "401 · UNAUTHORIZED"
          : statusCode === 403
            ? "403 · FORBIDDEN"
            : statusCode === 429
              ? "429 · THROTTLED"
              : `${statusCode}`
        : "ERR";
    const requestId =
      server.state === "denied" ? server.requestId : null;
    const throttled =
      server.state === "denied" && server.throttled;
    const retryAfterSeconds =
      server.state === "denied" ? server.retryAfterSeconds : null;
    return (
      <div className="min-h-screen bg-background">
        <NavigationBar />
        <main className="pt-14">
          <div className="max-w-xl mx-auto px-4 py-12">
            <Card className="border-tactical-red/40 bg-tactical-red/5" data-testid="require-sovereign-denied">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-sm font-mono uppercase tracking-widest text-tactical-red">
                  <ShieldAlert className="w-4 h-4" /> Access denied
                  <span
                    className="ml-auto text-[10px] font-mono px-2 py-0.5 rounded-sm border border-tactical-red/50 text-tactical-red bg-tactical-red/10"
                    data-testid="require-sovereign-status-code"
                    data-status={typeof statusCode === "number" ? statusCode : "ERR"}
                  >
                    {statusLabel}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs font-mono text-muted-foreground space-y-2">
                <p>This page is restricted to <span className="text-sovereign-gold">Sovereign tier</span> accounts.</p>
                <p>Your current tier: <span className="text-foreground">{tier ?? "unknown"}</span></p>
                {requestId && (
                  <p
                    data-testid="require-sovereign-request-id"
                    data-request-id={requestId}
                  >
                    Request id: <span className="text-foreground">{requestId}</span>
                  </p>
                )}
                {throttled && (
                  <div
                    data-testid="require-sovereign-throttle-hint"
                    data-retry-after={retryAfterSeconds ?? ""}
                    className="flex items-start gap-2 px-2 py-1.5 rounded-sm border border-sovereign-gold/40 bg-sovereign-gold/5 text-sovereign-gold"
                  >
                    <Clock3 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <div className="leading-relaxed">
                      <span className="uppercase tracking-widest text-[10px]">Throttled</span>
                      <p className="mt-0.5 text-[11px]">
                        Too many requests.{" "}
                        {retryAfterSeconds != null
                          ? `Try again in ${retryAfterSeconds}s.`
                          : "Wait a moment before retrying."}
                      </p>
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setReasonExpanded((v) => !v)}
                  className="flex items-center gap-1 text-[11px] uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="require-sovereign-reason-toggle"
                  aria-expanded={reasonExpanded}
                  aria-controls="require-sovereign-reason-body"
                >
                  {reasonExpanded ? (
                    <ChevronDown className="w-3 h-3" />
                  ) : (
                    <ChevronRight className="w-3 h-3" />
                  )}
                  Why access was denied
                </button>
                {reasonExpanded && (
                  <pre
                    id="require-sovereign-reason-body"
                    data-testid="require-sovereign-reason"
                    className="whitespace-pre-wrap break-words text-[11px] px-2 py-1.5 rounded-sm border border-tactical-red/30 bg-tactical-red/5 text-tactical-red"
                  >
                    {reason}
                  </pre>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  if (server.state !== "ok") {
    return (
      <div
        className="min-h-screen bg-background flex items-center justify-center"
        data-testid="require-sovereign-server-checking"
      >
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="w-6 h-6 text-sovereign-gold animate-spin" />
          <p className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
            Verifying admin access…
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}